/**
 * QA #263 fix #1 — Home banner priority queue.
 *
 * Problem: 5 banners (dayone / streak / badge / season-pass / monster-reveal)
 * could all stack on the home page simultaneously. For a 9-year-old, the first
 * impression became a wall of retention CTAs, drowning out the hero 出戰 /
 * 修煉學院 cards (Day-1 retention 命門, per market-insights memory).
 *
 * Fix: at most ONE banner per home render. Pick the highest-priority surface
 * (peeking at progress without consuming any one-shot markers). Lower-priority
 * banners stay queued in progress — they will surface on a subsequent home
 * render once the higher-priority slot clears.
 *
 * Priority order (highest first):
 *   1. dayone     — fresh kid: only surface this, hide everything else
 *   2. badge      — just earned, ride the dopamine while it's hot
 *   3. monster    — just caught
 *   4. streak     — daily milestone
 *   5. season-pass — long-tail tier track
 *
 * Important: badge/monster reveal banners call `consumeLastUnlocked` /
 * `consumeLastNew` which CLEAR the marker on read. To keep them queueable
 * we PEEK at the progress fields directly here; only the slot we choose
 * to surface gets the consume call (in home.ts via the existing renderer).
 *
 * Fresh-account rule (boss spec): a kid is "fresh" iff `dailyHook.firstLaunchAt`
 * has been seeded BUT D1 has not yet been claimed. In that state we ALWAYS
 * surface dayone and explicitly skip every other banner — first impression
 * must be a single, focused 🎁 moment.
 */

import type { PlayerProgress } from '../shared/types';
import { snapshotHook } from './dayone-hook';
import { snapshotStreak } from './streak/streak';
import { snapshotSeason } from './season-pass/season-pass';
import { isNextDayPreviewPending } from './components/NextDayPreviewCard';

/** A surfaceable banner slot. `none` = nothing to show. */
export type HomeBannerSlot =
  | 'chapter-mastered'
  | 'dayone'
  | 'badge'
  | 'monster'
  | 'streak'
  | 'season'
  | 'next-day-preview'
  | 'none';

/**
 * Mastery W4 — most recent unconsumed chapter-mastered event.
 *
 * The detector dispatches `'chapter:mastered'` on document; this module-load
 * listener captures the latest payload in memory + mirrors it to localStorage
 * (`mastery:chapterMastered:pending`) so a page reload still surfaces the
 * banner. The banner consumer (home.ts) clears both stores on consume.
 *
 * Kept module-local on purpose: the queue must be queryable WITHOUT importing
 * anything from src/game/ (學習與遊戲分離).
 */
const PENDING_KEY = 'mastery:chapterMastered:pending';

interface PendingChapterPayload {
  chapterId: string;
  subject: string;
  chapterTitle: string;
  masteredAt: string;
}

function readPendingChapter(): PendingChapterPayload | null {
  try {
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    const raw = ls?.getItem(PENDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PendingChapterPayload;
  } catch { return null; }
}

function writePendingChapter(p: PendingChapterPayload): void {
  try {
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    ls?.setItem(PENDING_KEY, JSON.stringify(p));
  } catch { /* quota / private mode */ }
}

/** Consume + clear the pending chapter-mastered banner payload. */
export function consumePendingChapterMastered(): PendingChapterPayload | null {
  const cur = readPendingChapter();
  if (!cur) return null;
  try {
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    ls?.removeItem(PENDING_KEY);
  } catch { /* noop */ }
  return cur;
}

let _chapterMasteredListenerAttached = false;
function attachChapterMasteredListener(): void {
  if (_chapterMasteredListenerAttached) return;
  if (typeof document === 'undefined') return;
  try {
    document.addEventListener('chapter:mastered', (raw: Event) => {
      const ev = raw as CustomEvent<{
        chapterId?: string;
        subject?: string;
        chapterTitle?: string;
        masteredAt?: string;
      }>;
      const d = ev.detail;
      if (!d?.chapterId) return;
      writePendingChapter({
        chapterId: d.chapterId,
        subject: d.subject ?? '',
        chapterTitle: d.chapterTitle ?? '',
        masteredAt: d.masteredAt ?? new Date().toISOString(),
      });
    });
    _chapterMasteredListenerAttached = true;
  } catch { /* headless */ }
}
attachChapterMasteredListener();

/** True iff the kid has been seeded but hasn't yet claimed D1 (first launch). */
export function isFreshAccount(p: PlayerProgress, nowMs: number = Date.now()): boolean {
  const snap = snapshotHook(p, nowMs);
  if (!snap.seeded) return true; // not even seeded — treat as fresh
  return snap.showGiftOverlay; // seeded + D1 still pending
}

/**
 * Compute up to TWO surfaceable banner slots in priority order. First entry is
 * the primary CTA, second (if any) is the secondary. Lower-priority banners
 * are deferred. For a fresh kid the list is always `['dayone']` — first
 * impression must be a single focused 🎁 moment.
 *
 * 9-year-old kids cannot triage 5 banners; capping at 2 keeps the home page
 * scannable.
 */
export function pickBannerSlots(
  p: PlayerProgress,
  nowMs: number = Date.now(),
  maxSlots: number = 2,
): HomeBannerSlot[] {
  // Fresh kid → dayone only.
  if (isFreshAccount(p, nowMs)) return ['dayone'];

  const queue: HomeBannerSlot[] = [];

  // 0. chapter-mastered — HIGHEST priority. The kid just unlocked a whole
  //    chapter, this is the loudest dopamine moment of the week and trumps
  //    every retention CTA below.
  if (readPendingChapter()) {
    queue.push('chapter-mastered');
  }

  // 1. dayone — has unclaimed days OR tutorial boss pending (and not fullyComplete).
  const dayone = snapshotHook(p, nowMs);
  if (!dayone.fullyComplete && (dayone.pendingDays.length > 0 || dayone.tutorialBossPending)) {
    queue.push('dayone');
  }

  // 2. badge — peek at lastUnlocked WITHOUT consuming (consumer is the banner).
  if (p.badges?.lastUnlocked) {
    queue.push('badge');
  }

  // 3. monster — peek at lastNew WITHOUT consuming.
  if (p.monsters?.lastNew) {
    queue.push('monster');
  }

  // 4. streak — surface only when there's a meaningful state to share
  //    (milestone crossed OR at-risk warning). Bare "你今天連續 5 天" is noise
  //    when the kid sees it daily.
  const streak = snapshotStreak(p, nowMs);
  if (streak.justCrossedMilestone !== null || streak.atRisk) {
    queue.push('streak');
  }

  // 5. season — surface only when there are tier rewards waiting to claim
  //    OR a reset happened (handled separately as a modal in home.ts).
  const season = snapshotSeason(p, nowMs);
  if (season.seeded && season.pendingCount > 0) {
    queue.push('season');
  }

  // 6. next-day-preview — kid just finished a quiz session (pending flag set by
  //    paper-view side-effects), AND not already surfaced today (gate). Lowest
  //    priority so it doesn't elbow out badge/monster/streak dopamine; the gate
  //    persists across renders so it stays queued until a render slot opens.
  if (isNextDayPreviewPending(nowMs)) {
    queue.push('next-day-preview');
  }

  return queue.slice(0, Math.max(0, maxSlots));
}
